'use client';

import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { SignalCard } from './SignalCard';
import { FilterIcon, Search01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { Signal, SignalsSearchData } from './types';
import type { OnOpenChatObject } from '../../hitl-types';

const DISPLAY_LIMIT = 10;

type SignalsRendererProps = {
  signals: Signal[];
  maxHeight?: string;
  variant?: 'compact' | 'detailed';
  query?: string;
  totalResults?: number;
  scoreThreshold?: number;
  onOpenObject?: OnOpenChatObject;
};

/**
 * Unified SignalsRenderer that handles both search results and HITL views
 */
export function SignalsRenderer({
  signals,
  maxHeight = '400px',
  variant = 'compact',
  query,
  totalResults,
  scoreThreshold,
  onOpenObject,
}: SignalsRendererProps) {
  const t = useTranslations('designSystem.chat.signalsRenderer');
  const isDetailed = variant === 'detailed';
  const displayedSignals = signals.slice(0, DISPLAY_LIMIT);
  const totalCount = totalResults ?? signals.length;
  const hasMore = totalCount > DISPLAY_LIMIT;
  const hasSearchHeader = query !== undefined || totalResults !== undefined;
  const showCompactHeader = !isDetailed && !hasSearchHeader;

  return (
    <VStack alignItems="start" gap={1.5} width="100%">
      {hasSearchHeader && (
        <HStack
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          width="100%"
          py={1}
          px={2}
          borderRadius="md"
        >
          <Search01Icon size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
          {query && (
            <Text
              fontSize="xs"
              color="fg.default"
              fontWeight="medium"
              flex={1}
              lineClamp={1}
            >
              {query}
            </Text>
          )}
          <HStack gap={1.5} flexShrink={0}>
            {totalResults !== undefined && (
              <Badge variant="outline" size="sm">
                {`${displayedSignals.length}${hasMore ? ` / ${totalCount}` : ''} ${t('results')}`}
              </Badge>
            )}
            {scoreThreshold !== undefined && (
              <Badge
                variant="outline"
                size="sm"
                icon={<FilterIcon size={10} />}
              >
                {String(scoreThreshold)}
              </Badge>
            )}
          </HStack>
        </HStack>
      )}

      {showCompactHeader && (
        <HStack alignItems="center" gap={2}>
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {t('label')}
          </Text>
          <Badge size="sm" variant="emphasis">
            {totalCount}
          </Badge>
        </HStack>
      )}

      {isDetailed ? (
        <VStack
          gap={3}
          width="100%"
          maxHeight={maxHeight}
          overflowY="auto"
          overflowX="hidden"
          alignItems="stretch"
        >
          {displayedSignals.map((signal, index) => {
            const key =
              signal.id || signal.signalId || signal.endpointId || index;

            return (
              <SignalCard
                key={key}
                signal={signal}
                variant={variant}
                onOpenObject={onOpenObject}
              />
            );
          })}
        </VStack>
      ) : (
        <VStack
          gap={0.5}
          width="100%"
          maxHeight={maxHeight}
          overflowY="auto"
          overflowX="hidden"
          alignItems="stretch"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="sm"
          p={1}
        >
          {displayedSignals.map((signal, index) => {
            const key =
              signal.id || signal.signalId || signal.endpointId || index;

            return (
              <SignalCard
                key={key}
                signal={signal}
                variant={variant}
                onOpenObject={onOpenObject}
              />
            );
          })}
        </VStack>
      )}

      {hasMore && (
        <Box px={2} width="100%">
          <Text fontSize="2xs" color="fg.muted">
            {t('showingResults', {
              displayed: displayedSignals.length,
              total: totalCount,
            })}
          </Text>
        </Box>
      )}
    </VStack>
  );
}

const readStringField = (
  data: Record<string, unknown>,
  keys: readonly string[],
): string | undefined => {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return undefined;
};

const readRecordField = (
  data: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined => {
  const value = data[key];

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
};

/**
 * Parse raw data into Signal format
 * Handles both compact (search results) and detailed (HITL) fields
 */
export function parseSignalData(data: Partial<Signal>): Signal | null {
  if (!data || typeof data !== 'object') return null;
  const dataRecord = data as Record<string, unknown>;
  const metadataRecord = readRecordField(dataRecord, 'metadata');
  const nestedSignalRecord = readRecordField(dataRecord, 'signal');

  // Must have at least one identifying field
  if (!data.title && !data.description && !data.category && !data.hypothesis) {
    return null;
  }

  return {
    // Core identifiers
    id:
      data.id ??
      readStringField(dataRecord, ['_id', 'objectId']) ??
      (nestedSignalRecord
        ? readStringField(nestedSignalRecord, ['id', '_id'])
        : undefined),
    signalId:
      data.signalId ??
      readStringField(dataRecord, ['signal_id', 'signalObjectId']) ??
      (metadataRecord
        ? readStringField(metadataRecord, ['signalId', 'signal_id', 'id'])
        : undefined),
    endpointId:
      data.endpointId ??
      (metadataRecord
        ? readStringField(metadataRecord, ['endpointId', 'endpoint_id'])
        : undefined),
    harEntryId:
      data.harEntryId ??
      (metadataRecord
        ? readStringField(metadataRecord, ['harEntryId', 'har_entry_id'])
        : undefined),
    entryPointId:
      data.entryPointId ??
      (metadataRecord
        ? readStringField(metadataRecord, ['entryPointId', 'entry_point_id'])
        : undefined),
    // Display fields
    title: data.title,
    signalTitle: data.signalTitle,
    description: data.description,
    content: data.content,
    url: data.url,
    method: data.method,
    statusCode: data.statusCode,
    severity: data.severity,
    category: data.category,
    score: data.score,
    rank: data.rank,
    // Detailed fields
    operation: data.operation,
    hypothesis: data.hypothesis,
    analysis: data.analysis,
    confidence: data.confidence,
    reason: data.reason,
    isAuthenticated: data.isAuthenticated,
    isAppWide: data.isAppWide,
    stage: data.stage,
    status: data.status,
    label: data.label,
    // Metadata
    metadata: data.metadata,
  };
}

/**
 * Parse signals list from search results
 */
export function parseSignalsListData(data: {
  results?: Partial<Signal>[];
  signals?: Partial<Signal>[];
}): Signal[] | null {
  if (!data || typeof data !== 'object') return null;
  // Check for signals array in results
  const resultsArray = data.results || data.signals;

  if (Array.isArray(resultsArray)) {
    return resultsArray
      .map((item) => parseSignalData(item))
      .filter((item): item is Signal => item !== null);
  }

  return null;
}

type SignalsSearchInput = {
  results?: Partial<Signal>[];
  signals?: Partial<Signal>[];
  query?: string;
  totalResults?: number;
  scoreThreshold?: number;
};

/**
 * Parse signals search data with metadata
 */
export function parseSignalsSearchData(
  data: SignalsSearchInput,
): SignalsSearchData | null {
  if (!data || typeof data !== 'object') return null;
  const signals = parseSignalsListData(data);

  if (!signals) return null;

  return {
    signals,
    query: data.query,
    totalResults: data.totalResults,
    scoreThreshold: data.scoreThreshold,
  };
}
